import { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { Header } from '@components/Header';
import { useDatadogAddError } from '@hooks';
import { useApplicationData, useStepperActions } from '@provider';
import { Button } from '@spark-web/button';
import { Container } from '@spark-web/container';
import { Heading } from '@spark-web/heading';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useQueryClient } from '@tanstack/react-query';
import { CONTACT_NUMBER_BY_FINANCIAL_PRODUCT, LABELS } from '@utils';
import ErrorImage from '../../../assets/error.svg?react';
import { getErrorDetails } from './helper';

export default function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { clearCredentials } = useAuth0();
  const { logError } = useDatadogAddError();
  const { onEdit } = useStepperActions();
  const { code, message } = getErrorDetails(error);
  const isUnauthenticated = message === 'UNAUTHENTICATED';

  const quote = useApplicationData('quote');
  const financialProductId = quote?.financialProduct?.id ?? 'default';
  const contactNumber =
    CONTACT_NUMBER_BY_FINANCIAL_PRODUCT[financialProductId] ?? CONTACT_NUMBER_BY_FINANCIAL_PRODUCT['default'];

  const queryClient = useQueryClient();

  const handleConfirm = () => {
    clearCredentials();
    queryClient.removeQueries();
    onEdit(0);
    resetErrorBoundary();
  };

  useEffect(() => {
    logError(error);
  }, [logError, error]);

  return (
    <>
      <Header financialProductId={financialProductId} />
      {isUnauthenticated ? (
        <ContentDialog title="Login" isOpen={true} onToggle={handleConfirm}>
          <Text>
            Your session has expired or you have been logged out. Please log in again to continue the application.
          </Text>
          <Button onClick={handleConfirm}>{LABELS.CONTINUE}</Button>
        </ContentDialog>
      ) : (
        <Container size="medium">
          <Stack gap="xxlarge" padding="xlarge" marginBottom="xlarge">
            <Stack align="center" gap="xxlarge" marginTop="xxlarge">
              <ErrorImage />
            </Stack>
            <Stack align="center" gap="xxlarge">
              <Heading level="1">An error occurred</Heading>
              <Text align="center">{message}</Text>
              <Text>If you’re still experiencing issues, call the team at Brighte on {contactNumber}</Text>
              {code && <Text tone="muted">Technical error {code}</Text>}
            </Stack>
          </Stack>
        </Container>
      )}
    </>
  );
}
